import React, { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getMySchedule, setMyAvailability } from '@/api/TournamentManagement/refereeApi';
import { RefereeAvailability } from '@/models/tournament';
import type { RefereeScheduleView } from '@/models/tournament';
import ScheduleCalendar from '@/components/tournament/ScheduleCalendar';
import { useToast, ToastRenderer } from '@/hooks/useToast';
import Button from '@/components/ui/Button';
import Icon from '@/components/AppIcon';

const MyScheduleTab: React.FC = () => {
  const { t } = useTranslation('tournament');
  const { toast, showToast, dismissToast } = useToast();

  const [entries, setEntries] = useState<RefereeScheduleView[]>([]);
  // Start as true — spinner shows immediately before ScheduleCalendar's
  // onRangeChange fires on mount and triggers the first fetch.
  const [calendarLoading, setCalendarLoading] = useState(true);
  // Set when getMySchedule returns 404 (no referee profile).
  const [noProfile, setNoProfile] = useState(false);
  // Anything that is NOT a 404 used to be swallowed whole: the catch only looked at 404 and the
  // non-404 `else` branch set nothing, so a 500 or a dropped connection left the calendar rendering
  // an empty month with no message at all — the referee read "no matches" from a failed request.
  const [loadError, setLoadError] = useState<string | null>(null);

  // The last range the calendar asked for, so "Try Again" can re-run the SAME fetch instead of
  // guessing a month.
  const lastRangeRef = useRef<{ start: string; end: string } | null>(null);

  const handleRangeChange = useCallback(async (start: string, end: string) => {
    lastRangeRef.current = { start, end };
    setCalendarLoading(true);
    setLoadError(null);
    try {
      const result = await getMySchedule(start, end);
      if (result.errors.length === 0 && result.payload) {
        setEntries(result.payload);
        setNoProfile(false);
      } else if (result.status === 404) {
        setNoProfile(true);
      } else {
        setLoadError(result.errors[0]?.description || t('Error loading schedule'));
      }
    } catch (err: any) {
      if (err?.status === 404) {
        setNoProfile(true);
      } else {
        setLoadError(err?.errors?.[0]?.description || err?.message || t('Error loading schedule'));
      }
    }
    finally { setCalendarLoading(false); }
  }, [t]);

  const retry = useCallback(() => {
    const range = lastRangeRef.current;
    if (range) handleRangeChange(range.start, range.end);
  }, [handleRangeChange]);

  const handleSaveAvailability = async (changes: { date: string; status: RefereeAvailability }[]) => {
    try {
      const result = await setMyAvailability(changes);
      if (result.errors.length === 0 && result.payload) {
        setEntries(result.payload);
        showToast(t('Availability updated successfully'), 'success');
      } else {
        showToast(result.errors[0]?.description || t('Failed to update availability'), 'error');
        throw new Error('save failed');
      }
    } catch (err: any) {
      if (err?.status === 404) {
        setNoProfile(true);
      } else if (err?.errors?.[0]?.description) {
        showToast(err.errors[0].description, 'error');
      }
      throw new Error('save failed');
    }
  };

  if (noProfile) {
    return (
      <div className="bg-card border border-border rounded-lg p-12 text-center" data-testid="no-profile-schedule">
        <Icon name="UserX" size={48} className="mx-auto text-muted-foreground/40 mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">{t('You do not have a referee profile')}</h3>
        <p className="text-muted-foreground">{t('Contact an administrator to create your referee profile.')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="my-schedule-tab">
      {loadError && (
        <div
          role="alert"
          className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3"
          data-testid="my-schedule-error"
        >
          <Icon name="AlertCircle" size={20} className="text-red-600 dark:text-red-400 flex-shrink-0" />
          <span className="text-sm text-red-700 dark:text-red-300">{loadError}</span>
          <Button variant="outline" size="sm" onClick={retry} className="ms-auto" data-testid="my-schedule-retry-btn">
            {t('Try Again')}
          </Button>
        </div>
      )}
      <ScheduleCalendar
        entries={entries}
        loading={calendarLoading}
        readOnly={false}
        onRangeChange={handleRangeChange}
        onSaveAvailability={handleSaveAvailability}
      />
      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </div>
  );
};

export default MyScheduleTab;
